import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Box,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import dayjs from "dayjs";
import { CategoryItem, useCategories } from "../hooks/useCategories";

export interface CategorySelectDialogProps {
  open: boolean;
  onClose: () => void;
  /** Valore attualmente selezionato (value della categoria). */
  value: string;
  /** Chiamato quando l'utente conferma: (categoria, { title?, publicationDate? }?). */
  onConfirm: (
    value: string,
    extra?: { title?: string; publicationDate?: string | null }
  ) => void;
  title?: string;
  /** Se forniti, mostra campi Titolo e Data di pubblicazione (modale da menu tabella). */
  initialTitle?: string;
  initialPublicationDate?: string | null;
  /**
   * Se fornito, usa questo elenco (già filtrato, es. per permessi utente) invece
   * di richiamare useCategories() internamente. In questo caso non viene
   * aggiunta la voce sintetica "Altro": l'utente deve scegliere solo tra le
   * categorie esplicitamente fornite.
   */
  categories?: CategoryItem[];
}

export const CategorySelectDialog: React.FC<CategorySelectDialogProps> = ({
  open,
  onClose,
  value,
  onConfirm,
  title = "Seleziona categoria",
  initialTitle,
  initialPublicationDate,
  categories: categoriesOverride,
}) => {
  const { data: fetchedCategories = [], isLoading: fetchLoading } =
    useCategories();
  const isLoading = categoriesOverride ? false : fetchLoading;
  const categoriesWithOther = React.useMemo(() => {
    if (categoriesOverride) return categoriesOverride;
    const normalized = fetchedCategories.filter(
      (c) => c.value.trim().toLowerCase() !== "altro"
    );
    return [
      ...normalized,
      {
        value: "altro",
        label: "Altro",
        description: "Categoria generica.",
      },
    ];
  }, [fetchedCategories, categoriesOverride]);
  const [selectedValue, setSelectedValue] = React.useState(value);
  const [editTitle, setEditTitle] = React.useState("");
  const [editPublicationDate, setEditPublicationDate] = React.useState("");

  const allowEditTitleDate = initialTitle !== undefined;

  React.useEffect(() => {
    if (open) {
      setSelectedValue(value);
      if (allowEditTitleDate) {
        setEditTitle(initialTitle ?? "");
        const dateVal = initialPublicationDate ?? "";
        setEditPublicationDate(
          typeof dateVal === "string" ? dateVal.slice(0, 10) : ""
        );
      }
    }
  }, [open, value, initialTitle, initialPublicationDate, allowEditTitleDate]);

  const handleConfirm = () => {
    if (allowEditTitleDate) {
      onConfirm(selectedValue, {
        title: editTitle.trim() || undefined,
        publicationDate: editPublicationDate.trim() || null,
      });
    } else {
      onConfirm(selectedValue);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {allowEditTitleDate && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, my: 1 }}>
            <TextField
              label="Titolo"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              fullWidth
            />
            <DatePicker
              label="Data di pubblicazione"
              value={editPublicationDate ? dayjs(editPublicationDate) : null}
              onChange={(newValue) =>
                setEditPublicationDate(
                  newValue ? newValue.format("YYYY-MM-DD") : ""
                )
              }
            />
          </Box>
        )}
        {allowEditTitleDate && (
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Categoria
          </Typography>
        )}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <RadioGroup
              value={selectedValue}
              onChange={(e) => setSelectedValue(e.target.value)}
            >
              {categoriesWithOther.map((cat) => (
                <Accordion
                  key={cat.value}
                  disableGutters
                  sx={{
                    "&:before": { display: "none" },
                    boxShadow: "none",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <AccordionSummary
                    sx={{
                      minHeight: 40,
                      py: 0,
                      "& .MuiAccordionSummary-content": { my: 0.75 },
                    }}
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{ color: "text.secondary", fontSize: 24 }}
                        aria-label="Espandi"
                      />
                    }
                  >
                    <FormControlLabel
                      value={cat.value}
                      control={<Radio />}
                      label={cat.label}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ mr: 1 }}
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 2, py: 1, pb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {cat.description || "—"}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button onClick={handleConfirm} variant="contained">
          Conferma
        </Button>
      </DialogActions>
    </Dialog>
  );
};
